"use client";

import { useCallback, useRef, useState } from "react";
import { Input } from "@workspace/ui/components/input";
import { X, FileText } from "lucide-react";
import { cn } from "@workspace/ui/lib/utils";
import { apiClient } from "@/lib/api-client";
import useSWR from "swr";
import { DOC_TYPE_LABELS } from "@/lib/frontmatter/types";
import type { DocType } from "@/lib/frontmatter/types";
import Link from "next/link";

interface DocSearchResult {
  id: string;
  title: string;
  frontmatterId: string | null;
  type: DocType | null;
  status: string | null;
}

const docSearchFetcher = async (url: string) => {
  const result = await apiClient.safeRequest<{ results: DocSearchResult[] }>(
    url,
  );
  return result.ok ? result.data : { results: [] };
};

// ─── Single doc chip (view mode) ─────────────────────────────────────────────

interface DocChipViewProps {
  docId: string;
  onRemove?: () => void;
}

export function DocChipView({ docId, onRemove }: DocChipViewProps) {
  // Try to resolve the doc to get title/type
  const { data } = useSWR(
    `/api/documents/search?q=${encodeURIComponent(docId)}&limit=1`,
    docSearchFetcher
  );
  const resolved = data?.results?.[0];

  const label = resolved?.frontmatterId || docId;
  const typeLabel = resolved?.type
    ? DOC_TYPE_LABELS[resolved.type]
    : null;

  return (
    <span className="inline-flex items-center gap-1.5 pl-1.5 pr-1 py-0.5 rounded bg-muted text-sm font-medium group">
      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      {resolved ? (
        <Link
          href={`/${resolved.id}`}
          className="truncate max-w-[180px] hover:underline text-primary"
          title={resolved.title}
        >
          {typeLabel ? `${typeLabel}: ` : ""}
          {resolved.title}
        </Link>
      ) : (
        <span className="truncate max-w-[180px] text-muted-foreground">
          {label}
        </span>
      )}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-sm hover:bg-foreground/10 p-0.5"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

// ─── Doc typeahead ───────────────────────────────────────────────────────────

interface DocTypeaheadProps {
  onSelect: (docId: string) => void;
  exclude?: string[];
  placeholder?: string;
}

function DocTypeahead({
  onSelect,
  exclude = [],
  placeholder = "Search docs...",
}: DocTypeaheadProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data } = useSWR(
    query.length >= 2
      ? `/api/documents/search?q=${encodeURIComponent(query)}&limit=8`
      : null,
    docSearchFetcher,
    { dedupingInterval: 300 }
  );

  const results = (data?.results ?? []).filter(
    (d) => !exclude.includes(d.frontmatterId ?? d.id)
  );

  const handleSelect = useCallback(
    (result: DocSearchResult) => {
      onSelect(result.frontmatterId ?? result.id);
      setQuery("");
      setOpen(false);
    },
    [onSelect]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      handleSelect(results[highlightIndex]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  // Reset the highlight whenever the result set changes size, adjusted DURING
  // RENDER rather than in an effect. An effect would commit one render with the
  // old index still pointing into the new list, so an Enter landing in that gap
  // would select the wrong doc; React re-runs this component on the set below
  // before any handler can fire.
  const resultsLen = results.length;
  const [lastResultsLen, setLastResultsLen] = useState(resultsLen);
  if (resultsLen !== lastResultsLen) {
    setLastResultsLen(resultsLen);
    setHighlightIndex(0);
  }

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(e.target.value.length >= 2);
        }}
        onFocus={() => query.length >= 2 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 200)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="h-8 text-sm px-2"
      />
      {open && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
          {results.map((doc, i) => (
            <button
              key={doc.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(doc)}
              className={cn(
                "flex items-center gap-2 w-full px-2 py-1.5 text-sm text-left hover:bg-accent",
                i === highlightIndex && "bg-accent"
              )}
            >
              <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="truncate">{doc.title}</div>
                {doc.frontmatterId && (
                  <div className="text-sm text-muted-foreground">
                    {doc.frontmatterId}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Doc links field (array of doc IDs) ──────────────────────────────────────

interface DocLinkChipsProps {
  value?: string[];
  editing?: boolean;
  onChange?: (ids: string[]) => void;
}

export function DocLinkChips({
  value = [],
  editing,
  onChange,
}: DocLinkChipsProps) {
  if (!editing && value.length === 0) {
    return null;
  }

  return (
    <div className="space-y-1">
      {value.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {value.map((id) => (
            <DocChipView
              key={id}
              docId={id}
              onRemove={editing ? () => onChange?.(value.filter((v) => v !== id)) : undefined}
            />
          ))}
        </div>
      ) : (
        <span className="text-sm text-muted-foreground italic">—</span>
      )}
      {editing && (
        <DocTypeahead
          onSelect={(id) => onChange?.([...value, id])}
          exclude={value}
        />
      )}
    </div>
  );
}

// ─── Single doc link (superseded_by) ─────────────────────────────────────────

interface SingleDocLinkProps {
  value?: string | null;
  editing?: boolean;
  onChange?: (id: string | null) => void;
}

export function SingleDocLink({
  value,
  editing,
  onChange,
}: SingleDocLinkProps) {
  if (!editing) {
    return value ? (
      <DocChipView docId={value} />
    ) : (
      <span className="text-sm text-muted-foreground italic">—</span>
    );
  }

  return (
    <div className="space-y-1">
      {value && (
        <DocChipView
          docId={value}
          onRemove={() => onChange?.(null)}
        />
      )}
      {!value && (
        <DocTypeahead
          onSelect={(id) => onChange?.(id)}
          exclude={value ? [value] : []}
        />
      )}
    </div>
  );
}
